<script setup>
// Libraries
import { ref, computed, reactive, onMounted, watch } from 'vue'
import { EventBus } from '@common/lib/event-bus'

// components
import CompetitorTile from '@src/modules/analytics_v3/components/CompetitorTile.vue'
import PerformanceComparison from '@src/modules/analytics_v3/components/PerformanceComparison.vue'
import CompetitorsTable from '@src/modules/analytics_v3/components/CompetitorsTable.vue'
import FollowersComparisonBarChart from '@src/modules/analytics_v3/components/FollowersComparisonBarChart.vue'
import SkeletonBox from '@src/modules/analytics/views/common/SkeletonBox.vue'
import AnalyticsCardWrapper from '@src/modules/analytics_v3/components/AnalyticsCardWrapper.vue'
import SpecificPostTypeChart from '@src/modules/analytics_v3/components/SpecificPostTypeChart.vue'
import CompetitorsTableComponent from '@src/modules/analytics_v3/components/CompetitorsTableComponent.vue'
import PostEngagementChart from '@src/modules/analytics_v3/components/PostEngagementChart.vue'
import PostEngagementOverTime from '@src/modules/analytics_v3/components/PostEngagementOverTime.vue'
import ReportFooter from '@src/modules/analytics/components/reports/ReportFooter.vue'
import PostReactsDistributionChart from '@src/modules/analytics_v3/components/PostReactsDistributionChart.vue'
import TopPerformingPostsReport from '@src/modules/analytics_v3/components/TopPerformingPostsReport.vue'
import LeastPerformingPostsReport from '@src/modules/analytics_v3/components/LeastPerformingPostsReport.vue'

// Constants
import { PLATFORMS } from '@src/modules/analytics_v3/constants/constants.js'

// Composables
import useCompetitorHelper from '@src/modules/analytics_v3/composables/useCompetitorHelper.js'
import useCompetitorReport from '@src/modules/analytics_v3/composables/useCompetitorReport.js'
import useDataTableMetrics from '@src/modules/analytics_v3/composables/useDataTableMetrics.js'
import usePostingActivityGraphByTypes from '@src/modules/analytics_v3/composables/usePostingActivityGraphByTypes.js'
import usePostingBySpecificType from '@src/modules/analytics_v3/composables/usePostingBySpecificType.js'
import useFollowersGrowthComparison from '@src/modules/analytics_v3/composables/useFollowersGrowthComparison.js'
import useTopHashtags from '@src/modules/analytics_v3/composables/useTopHashtags.js'
import useIndividualHashtagData from '@src/modules/analytics_v3/composables/useIndividualHashtagData.js'
import useBiographyData from '@src/modules/analytics_v3/composables/useBiographyData.js'
import useTopAndLeastPerformingPosts from '@src/modules/analytics_v3/composables/useTopAndLeastPerformingPosts.js'
import usePostEngagementByCompetitor from '@src/modules/analytics_v3/composables/usePostEngagementByCompetitor.js'
import usePostEngagementOverTime from '@src/modules/analytics_v3/composables/usePostEngagementOverTime.js'
import usePostReactsDistributionByCompany from '@src/modules/analytics_v3/composables/usePostReactsDistributionByCompany.js'
import useCompetitorsFactory from '@src/modules/analytics_v3/composables/useCompetitorsFactory.js'

// Props
const props = defineProps({
  platform: {
    type: String,
    default: PLATFORMS.FACEBOOK,
  },
  competitorReportId: {
    type: [String, Boolean],
    default: false
  },
  reportDate: {
    type: String,
    default: ''
  }
})

const { getMinMax, getComponentInfoTooltip } = useCompetitorHelper()
const { platform: platformName } = useCompetitorsFactory()

const {
  getDataTableMetrics,
  dataTableMetricsResponse,
  dataTableMetricsLoadingState,
} = useDataTableMetrics()
const {
  getPostingActivityGraphByTypes,
  postingActivityGraphByTypesResponse,
  dateRangeDiff,
  mediaType,
} = usePostingActivityGraphByTypes()
const {
  getPostingActivityBySpecificType,
  postingActivitySpecificTypeLoadingState: postActivitySpecificTypeLoadingState,
  postingActivitySpecificTypeResponse
} = usePostingBySpecificType()
const {
  getFollowersGrowthComparison,
  followersGrowthComparisonResponse,
  followersGrowthComparisonLoadingState,
} = useFollowersGrowthComparison()
const { getTopHashtags, topHashtagsResponse, topHashtagsLoadingState } =
  useTopHashtags()
const { getIndividualHashtag, individualHashtagDataResponse } =
  useIndividualHashtagData()
const { getBiographyData, biographyDataResponse, biographyDataLoadingState } =
  useBiographyData()
const {
  getTopAndLeastPerformingPosts,
  topAndLeastPerformingPostsResponse,
  topAndLeastPerformingPostsLoadingState,
} = useTopAndLeastPerformingPosts()
const {
  getPostEngagementByCompetitorData,
  postEngagementByCompetitorResponse,
  postEngagementByCompetitorLoadingState,
} = usePostEngagementByCompetitor()
const {
  getPostEngagementOverTimeData,
  postEngagementOverTimeResponse,
  postEngagementOverTimeLoadingState,
} = usePostEngagementOverTime()
const {
  getPostReactsDistributionByCompanyData,
  postReactsDistributionByCompanyResponse: getPostReactsDistribution,
  postReactsDistributionByCompanyLoadingState,
} = usePostReactsDistributionByCompany()

const { getReportInfo, reportId, setDateValues, reportCompetitorsInfo } = useCompetitorReport(props.competitorReportId)

const reactResponseArray = ref([])
const engagementOverTime = ref([])
const specificTypeResponse = ref([])

const selectedPostType = ref('Photo')
const selectedView = ref('chart')
const state = reactive({
  maxER: 0,
  minER: 0,
  maxFollowers: 0,
  minFollowers: 0,
})

// Computed properties
const imgSrcChart = computed(() => {
  return selectedView.value === 'chart'
    ? '/assets/icons/blue-chart.svg'
    : '/assets/icons/chart.svg'
})

const imgSrcTable = computed(() => {
  return selectedView.value === 'table'
    ? '/assets/icons/blue-table.svg'
    : '/assets/icons/table.svg'
})

const isAllDataFetched = computed(() => !dataTableMetricsLoadingState.value)

const fetchSingleHashtagData = (hashtag = '') => {
  getIndividualHashtag(PLATFORMS.FACEBOOK, hashtag)
}

// Data for the performance comparison graph
const performanceComparisonData = computed(() => ({
  title: "Competitors' Performance Comparison",
  xAxisLabel: 'Engagement Rate (%)',
  yAxisLabel: 'Followers',
}))

// Data for performance review table
const performanceReviewTableData = computed(() => ({
  title: "Competitors' Comparative Table",
  type: 'performanceReviewTable',
  headers: [
    'followersCount',
    // 'followersCountDiff',
    'averagePostsPerWeek',
    // 'averagePostsPerWeekDiff',
    'engagementRate',
    // 'engagementRateDiff',
  ],
}))

// Data for performance review graph
const performanceReviewGraphData = computed(() => ({
  title: 'Followers Comparison',
  xAxisLabel: 'Competitors',
  yAxisLabel: 'Followers',
}))

// Data for hashtags table
const hashtagsTableData = {
  headers: [
    'tag',
    'companies_using',
    'count',
    'engagement_per_post',
    'engagement_per_follower',
    'engagement_rate_by_follower',
  ],
  type: 'mostEngagedHashtagsTable',
  title: 'Most Engaged Hashtags',
}

// Data for bio analysis table
const bioAnalysisTableData = {
  headers: ['biography', 'biography_length'],
  type: 'bioAnalysisTableData',
  title: 'Bio Analysis',
}

// data for specific activity graph, maps the colors w.r.t specific competitors & then filter on the basis of state.
const postingBySpecificType = computed(() => {
  const data = postingActivitySpecificTypeResponse.value
    ?.map((item) => {
      for (const competitor of dataTableMetricsResponse.value) {
        if (competitor.name === item.name) {
          if (competitor.state === 'Added') {
            return null
          }
          return { ...item, color: competitor.color }
        }
      }
      return null
    })
    .filter((competitor) => competitor && competitor.color)

  return {
    xAxisLabel: 'Competitors',
    yAxisLabel: 'Average Engagement',
    yAxisSecondLabel: 'Posts/ Week',
    title: 'Post Performance',
    dateRangeDiff: dateRangeDiff.value,
    data,
  }
})

// data for single hashtag data w.r.t competitors
const singleHashtagContent = {
  headers: [
    'name',
    'count',
    'engagement_per_post',
    'engagement_per_follower',
    'engagement_rate_by_follower',
  ],
  type: 'singleHashtagTableData',
}

const postEngagementByCompetitorData = computed(() => ({
  title: 'Post Engagements By Competitor',
  xAxisLabel: 'Competitors',
  yAxisLabel: 'Engagement',
  yAxisSecondLabel: 'Number of Posts',
}))

const postEngagementOverTimeData = computed(() => ({
  title: 'Post Engagement Over Time',
  xAxisLabel: 'Date',
  yAxisLabel: 'Posts',
  yAxisSecondLabel: 'Engagement',
}))

const getPostEngagementOverTime = computed(() => {
  if (postEngagementByCompetitorResponse?.length) {
    return postEngagementOverTimeResponse
  }
  return []
})

// Total pages for pagination
const totalPages = computed(() => 11) // Adjusted to include bio analysis, top and least performing posts pages
// Derived computed properties
const filteredDataTableMetrics = computed(() => dataTableMetricsResponse.value || [])

// This computed property returns all the available posts types of competitors in report. /
const getAllAvailablePostsTypes = computed(() =>
  postingActivityGraphByTypesResponse.value?.map((item) => item.mediaType)
)

const getPerformanceComparisonLegends = computed(() => {
  return filteredDataTableMetrics.value.map(item => ({
    name: item.name || item.slug,
    color: item.color,
  }))
})

const getBioAnalysisTableData = computed(() =>
  biographyDataResponse.value?.filter((competitor) => competitor.state !== 'Added')
)

const changePostTypeChart = (value) => {
  if (value) {
    selectedPostType.value = value
    getPostingActivityBySpecificType(value)
  }
}

const getTileGraphValue = (index) => {
  if (
    dataTableMetricsResponse.value[index] &&
    dataTableMetricsResponse.value[index].engagementRate
  ) {
    const calculatedER = dataTableMetricsResponse.value[index].engagementRate
    if (calculatedER >= 1.5) return 'green'
    if (calculatedER >= 1) return 'yellow'
    return 'red'
  }
  return 'green'
}

// data for followers growth comparison graph
const getFollowersGrowthData = computed(() => ({
  data: followersGrowthComparisonResponse.value,
  xAxisLabel: 'Timeline',
  yAxisLabel: 'Followers',
  title: 'Followers Growth Comparison',
  isLoading: followersGrowthComparisonLoadingState.value,
}))

// Lifecycle Hooks
onMounted(async () => {
  platformName.value = 'facebook'

  setDateValues(props.reportDate)
  await getReportInfo()
  getDataTableMetrics('facebook', reportId.value)
  getPostingActivityGraphByTypes('facebook', reportId.value)
  getFollowersGrowthComparison('facebook', reportId.value)
  getBiographyData('facebook', reportId.value)
  getTopHashtags('facebook', reportId.value)
  getPostEngagementByCompetitorData('facebook', reportId.value)
  getTopAndLeastPerformingPosts('facebook', reportId.value)
  for (const competitor of reportCompetitorsInfo.value.competitors) {
    const reactsData = await getPostReactsDistributionByCompanyData('facebook', competitor.competitor_id)
    // If the response is empty, create a new array with just the page_name
    // Otherwise, add the page_name to the first item in the array
    const dataWithPageName = !reactsData || reactsData.length === 0
      ? [{ page_name: competitor.name }]
      : reactsData.map((item, index) => index === 0 ? { ...item, page_name: competitor.name } : item)

    reactResponseArray.value.push(dataWithPageName)
    const engResponse = await getPostEngagementOverTimeData('facebook', competitor.competitor_id)
    engagementOverTime.value.push({data: engResponse, page_name: competitor.name})
  }

  // loop over getAllAvailablePostsTypes and getPostReactsDistributionByCompanyData for each type
  for (const type of getAllAvailablePostsTypes.value) {
    const postTypeSpecificData = await getPostingActivityBySpecificType('facebook', type, '', reportId.value)
    // If the response is empty, create a new array with just the page_name
    // Otherwise, add the page_name to the first item in the array

    specificTypeResponse.value.push(postTypeSpecificData)
  }
})


watch(
  () => dataTableMetricsResponse.value,
  (newVal) => {
    state.loading = false
    if (newVal) {
      const engagementRate = getMinMax(newVal, 'engagementRate')
      const followers = getMinMax(newVal, 'followersCount')
      state.maxFollowers = followers.max
      state.minFollowers = followers.min
      state.maxER = engagementRate.max
      state.minER = engagementRate.min
    }
  }
)

</script>

<template>
  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">Competitor Overview</h2>

      <div
        v-if="!dataTableMetricsResponse.length"
        class="grid grid-cols-5 gap-7 mb-7"
      >
        <SkeletonBox class="!h-[8.5rem]" />
        <SkeletonBox class="!h-[8.5rem]" />
        <SkeletonBox class="!h-[8.5rem]" />
        <SkeletonBox class="!h-[8.5rem]" />
        <SkeletonBox class="!h-[8.5rem]" />
      </div>
      <transition-group
        v-else-if="dataTableMetricsResponse.length"
        tag="div"
        class="grid grid-cols-5 gap-7 mb-7"
        name="slide-fade"
      >
        <template
          v-for="(competitor, index) in dataTableMetricsResponse"
          :key="`competitor_tile_${index}`"
        >
          <CompetitorTile
            :id="competitor.business_account_id"
            :name="competitor.name ? competitor.name : competitor.slug"
            :slug="competitor.slug"
            :image="competitor.image"
            :color="competitor.color"
            :followers="competitor.followersCount"
            :graph="getTileGraphValue(index)"
            :change="competitor.followersCountDiff"
            :state="competitor.state"
            :engagement-rate="competitor.engagementRate"
            :max-er="state.maxER"
            :min-er="state.minER"
            :max-followers="state.maxFollowers"
            :min-followers="state.minFollowers"
          />
        </template>
      </transition-group>

      <div class="mb-7 relative">
        <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">Performance Summary</h2>
        <PerformanceComparison
          :title="performanceComparisonData.title"
          :legend="{
            show: true,
            data: getPerformanceComparisonLegends,
          }"
          :x-axis-label="performanceComparisonData.xAxisLabel"
          :y-axis-label="performanceComparisonData.yAxisLabel"
          :data="filteredDataTableMetrics"
          type="performanceComparison"
          :allow-data-zoom="false"
        />
      </div>
    </div>
    <ReportFooter :current-page="1" :total-pages="totalPages" />
  </div>

  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <div class="mb-7">
        <CompetitorsTable
          :title="performanceReviewTableData.title"
          :data="filteredDataTableMetrics"
          :headers="performanceReviewTableData.headers"
          :followers-growth-data="getFollowersGrowthData"
          :platform-name="PLATFORMS.FACEBOOK"
          :type="performanceReviewTableData.type"
        />
      </div>

      <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">Followers Insights</h2>
      <div class="mb-7 grid grid-cols-1 gap-4">
          <FollowersComparisonBarChart
            :title="performanceReviewGraphData.title"
            :x-axis-label="performanceReviewGraphData.xAxisLabel"
            :y-axis-label="performanceReviewGraphData.yAxisLabel"
            :data="filteredDataTableMetrics"
            :platform-name="PLATFORMS.FACEBOOK"
            type="followersComparisonGraph"
          />
      </div>
    </div>
    <ReportFooter :current-page="2" :total-pages="totalPages" />
  </div>

  <div v-if="specificTypeResponse.length > 0" class="page_view flex flex-col justify-between">

    <div class="subpage">
      <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">Post Type Insights</h2>
      <!-- grid 2 col -->
      <div class="grid grid-cols-2 gap-4">
        <AnalyticsCardWrapper
          v-for="(specificTypeData, index) in specificTypeResponse.slice(0, 4)"
          :key="`specificTypeResponse_${index}`"
          type="specific-type-chart"
          :enable-modal="false"
        >
          <template v-slot:card-header>
            <div class="w-full flex justify-between items-center">
              <div class="flex items-center gap-2">
                <h2 class="text-base font-weight-500 select-none"><span class="capitalize"> {{ specificTypeData?.[0]?.mediaType }} </span> Post Performance </h2>
              </div>
            </div>
          </template>
          <template v-slot:card-body="slotProps">
            <SpecificPostTypeChart
              :key="Math.random()"
              :title="postingBySpecificType?.title"
              :date-range-diff="dateRangeDiff"
              :data="specificTypeData"
              :x-axis-label="postingBySpecificType?.xAxisLabel"
              :y-axis-label="postingBySpecificType?.yAxisLabel"
              :y-axis-second-label="postingBySpecificType?.yAxisSecondLabel"
              :platform-name="PLATFORMS.FACEBOOK"
              :is-modal="slotProps.isModal"
              type="specificPostTypeChart"
            />

          </template>
        </AnalyticsCardWrapper>
      </div>
    </div>
    <ReportFooter :current-page="3" :total-pages="totalPages" />
  </div>
  <div v-if="specificTypeResponse.length > 4" class="page_view flex flex-col justify-between">
    <div class="subpage">
      <!-- grid 2 col -->
      <div class="grid grid-cols-2 gap-4">
        <AnalyticsCardWrapper
          v-for="(specificTypeData, index) in specificTypeResponse.slice(4, specificTypeResponse.length)"
          :key="`specificTypeResponse_${index}`"
          type="specific-type-chart"
          :enable-modal="false"
        >
          <template v-slot:card-header>
            <div class="w-full flex justify-between items-center">
              <div class="flex items-center gap-2">
                <h2 class="text-base font-weight-500 select-none"><span class="capitalize"> {{ specificTypeData?.[0]?.mediaType }} </span> Post Performance</h2>
              </div>
            </div>
          </template>
          <template v-slot:card-body="slotProps">
            <SpecificPostTypeChart
              :key="Math.random()"
              :title="postingBySpecificType?.title"
              :date-range-diff="dateRangeDiff"
              :data="specificTypeData"
              :x-axis-label="postingBySpecificType?.xAxisLabel"
              :y-axis-label="postingBySpecificType?.yAxisLabel"
              :y-axis-second-label="postingBySpecificType?.yAxisSecondLabel"
              :platform-name="PLATFORMS.FACEBOOK"
              :is-modal="slotProps.isModal"
              type="specificPostTypeChart"
            />

          </template>
        </AnalyticsCardWrapper>
      </div>
    </div>
    <ReportFooter :current-page="4" :total-pages="totalPages" />
  </div>
  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">Hashtags Analysis</h2>
      <div class="mb-7">
        <CompetitorsTable
          :title="hashtagsTableData?.title"
          :show-info-cell="false"
          :data="topHashtagsResponse"
          :headers="hashtagsTableData?.headers"
          :type="hashtagsTableData?.type"
          :single-hashtag-data="individualHashtagDataResponse"
          :single-hashtag-header="singleHashtagContent.headers"
          :index-to-sort="2"
          :platform-name="PLATFORMS.FACEBOOK"
          @single-hashtag="fetchSingleHashtagData"
        />
      </div>
      <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">Post Engagement Comparison</h2>
      <div class="mb-7 grid grid-cols-2 gap-4">
        <AnalyticsCardWrapper
          type="post-engagement"
          :enable-modal="
            !!(
              postEngagementByCompetitorResponse &&
              postEngagementByCompetitorResponse?.length
            )
          "
        >
          <template v-slot:card-header>
            <div class="w-full flex justify-between items-center">
              <div class="flex items-center">
                <h2 class="text-base font-weight-500 select-none">Post Engagement Comparison</h2>
              </div>
            </div>
          </template>
          <template v-slot:card-body="slotProps">
            <PostEngagementChart
              :title="postEngagementByCompetitorData?.title"
              :x-axis-label="postEngagementByCompetitorData?.xAxisLabel"
              :y-axis-label="postEngagementByCompetitorData?.yAxisLabel"
              :y-axis-second-label="postEngagementByCompetitorData?.yAxisSecondLabel"
              :data="postEngagementByCompetitorResponse"
              :platform-name="PLATFORMS.FACEBOOK"
              :is-modal="slotProps.isModal"
              type="postEngagement"
            />
          </template>
        </AnalyticsCardWrapper>
        <AnalyticsCardWrapper
        v-if="!!engagementOverTime.length"
        type="post-engagement-time"
        :enable-modal="!!engagementOverTime.length"
      >
        <template v-slot:card-header>
          <div class="w-full flex justify-between items-center">
            <div class="flex items-center">
              <h2 class="text-base font-weight-500 select-none">Post Engagement Over Time for {{ engagementOverTime[0]?.page_name }}</h2>
            </div>
          </div>
        </template>
        <template v-slot:card-body="slotProps">
            <PostEngagementOverTime
              :title="postEngagementOverTimeData.title"
              :x-axis-label="postEngagementOverTimeData.xAxisLabel"
              :y-axis-label="postEngagementOverTimeData.yAxisLabel"
              :y-axis-second-label="postEngagementOverTimeData.yAxisSecondLabel"
              :data="engagementOverTime[0].data"
              :platform-name="PLATFORMS.FACEBOOK"
              :is-modal="slotProps.isModal"
              type="postEngagementTime"
              :allow-data-zoom="false"
            />
        </template>
      </AnalyticsCardWrapper>


      </div>

    </div>
    <ReportFooter :current-page="5" :total-pages="totalPages" />
  </div>

  <div v-if="engagementOverTime.length > 1" class="page_view flex flex-col justify-between">
    <div class="subpage">
      <div class="grid grid-cols-2 gap-4">
        <AnalyticsCardWrapper
        v-for="(response, index) in engagementOverTime.slice(1, engagementOverTime.length)" :key="index"
        type="post-engagement-time"
        :enable-modal="!!engagementOverTime.length"
      >
        <template v-slot:card-header>
          <div class="w-full flex justify-between items-center">
            <div class="flex items-center">
              <h2 class="text-base font-weight-500 select-none">Post Engagement Over Time for {{ response?.page_name }}</h2>
            </div>
          </div>
        </template>
        <template v-slot:card-body="slotProps">
            <PostEngagementOverTime
              :title="postEngagementOverTimeData.title"
              :x-axis-label="postEngagementOverTimeData.xAxisLabel"
              :y-axis-label="postEngagementOverTimeData.yAxisLabel"
              :y-axis-second-label="postEngagementOverTimeData.yAxisSecondLabel"
              :data="response.data"
              :platform-name="PLATFORMS.FACEBOOK"
              :is-modal="slotProps.isModal"
              :allow-data-zoom="false"
              type="postEngagementTime"
            />
        </template>
      </AnalyticsCardWrapper>
      </div>
    </div>
    <ReportFooter :current-page="6" :total-pages="totalPages" />
  </div>

  <div class="page_view flex flex-col justify-between">
    <div v-for="(response, index) in reactResponseArray.slice(0,2)" :key="index"  class="subpage">
      <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">Post Reactions Distribution in
        {{ response?.[0]?.page_name }}
      </h2>
      <div class="mb-7">
        <AnalyticsCardWrapper
          type="post-react-distribution"
          :enable-modal="
            !!(response && response.length)
          "
        >
          <template v-slot:card-header>
            <div class="w-full flex justify-between items-center">
              <div class="flex items-center">
              </div>
            </div>
          </template>
          <template v-slot:card-body="slotProps">
            <PostReactsDistributionChart

              :key="index"
              :data="response"
              :platform-name="PLATFORMS.FACEBOOK"
              :is-modal="slotProps.isModal"
              type="postReactDistribution"
            />
          </template>
        </AnalyticsCardWrapper>
      </div>
    </div>
    <ReportFooter :current-page="7" :total-pages="totalPages" />
  </div>

  <div v-if="reactResponseArray.slice(2, 4).length" class="page_view flex flex-col justify-between">
    <div v-for="(response, index) in reactResponseArray.slice(2, 4)" :key="index" class="subpage">
      <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">Post Reactions Distribution in
        {{ response?.[0]?.page_name }}
      </h2>
      <div class="mb-7">
        <AnalyticsCardWrapper
          type="post-react-distribution"
          :enable-modal="
            !!(response && response.length)
          "
        >
          <template v-slot:card-header>
            <div class="w-full flex justify-between items-center">
              <div class="flex items-center">
              </div>
            </div>
          </template>
          <template v-slot:card-body="slotProps">
            <PostReactsDistributionChart
              :key="index"
              :data="response"
              :platform-name="PLATFORMS.FACEBOOK"
              :is-modal="slotProps.isModal"
              type="postReactDistribution"
            />
          </template>
        </AnalyticsCardWrapper>
      </div>
    </div>
    <ReportFooter :current-page="8" :total-pages="totalPages" />
  </div>

  <div v-if="reactResponseArray.slice(4, 5).length" class="page_view flex flex-col justify-between">
    <div v-for="(response, index) in reactResponseArray.slice(4, 5)" :key="index" class="subpage">
      <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">Post Reactions Distribution
        {{ response?.[0]?.page_name }}
      </h2>
      <div class="mb-7">
        <AnalyticsCardWrapper
          type="post-react-distribution"
          :enable-modal="
            !!(response && response.length)
          "
        >
          <template v-slot:card-header>
            <div class="w-full flex justify-between items-center">
              <div class="flex items-center">
              </div>
            </div>
          </template>
          <template v-slot:card-body="slotProps">
            <PostReactsDistributionChart
              :key="index"
              :data="response"
              :platform-name="PLATFORMS.FACEBOOK"
              :is-modal="slotProps.isModal"
              type="postReactDistribution"
            />
          </template>
        </AnalyticsCardWrapper>
      </div>
    </div>
    <ReportFooter :current-page="9" :total-pages="totalPages" />
  </div>

  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <h2 class="bg-gradient-to-r from-[#01AAFF] to-[#0ACADD] py-2 px-3 rounded-lg text-white my-2 text-base font-weight-500">Page Bio Comparison</h2>
      <div class="mb-7">
        <CompetitorsTable
          :title="bioAnalysisTableData.title"
          :data="getBioAnalysisTableData"
          :headers="bioAnalysisTableData.headers"
          :platform-name="PLATFORMS.FACEBOOK"
          :type="bioAnalysisTableData.type"
        />
      </div>
    </div>
    <ReportFooter :current-page="10" :total-pages="totalPages" />
  </div>

  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <TopPerformingPostsReport :data="topAndLeastPerformingPostsResponse" :platform-name="PLATFORMS.FACEBOOK" />
    </div>
    <ReportFooter :current-page="11" :total-pages="totalPages" />
  </div>

  <div class="page_view flex flex-col justify-between">
    <div class="subpage">
      <LeastPerformingPostsReport :data="topAndLeastPerformingPostsResponse" :platform-name="PLATFORMS.FACEBOOK" />
    </div>
    <ReportFooter :current-page="12" :total-pages="totalPages" />
  </div>
</template>

<style scoped>
.page_view {
  page-break-after: always;
  min-height: 29.7cm;
}

.subpage {
  padding: 1cm;
  height: 100%;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}
</style>
